import { CRAWLER_HUB_PAGE_META, CRAWLER_HUB_PATH_ALIASES } from '../share/crawlerHubPageMeta.js';
import { resolveMarketingSiteOrigin, toAbsoluteSiteUrl } from './absoluteSiteUrl.js';
import { resolveDefaultMarketingOgImagePath } from './companyBranding.js';
import {
  isTenantMarketingBrand,
  resolveMarketingSiteCompanyName,
  substituteMarketingBrandingInText,
} from './marketingSiteContact.js';
import type { HubPageSeoEntry } from './publicMarketingConfig.js';

export const TB_MARKETING_SITE_ORIGIN = 'https://www.toolsandbubbles.com';

/** Configurable hub paths (super-admin / org public marketing settings). */
export const HUB_PAGE_SEO_LABELS: Record<string, string> = {
  '/our-story': 'Our Story',
  '/faq': 'FAQ',
  '/contact-us': 'Contact Us',
  '/testimonials': 'Testimonials',
  '/franchise': 'Franchise',
  '/careers': 'Careers',
  '/blog': 'Blog listing',
  '/insurance': 'Insurance',
  '/amc-services': 'AMC Services',
  '/property-management': 'Property Management',
  '/cleaning-chemicals': 'Cleaning Chemicals',
  '/our-staff': 'Our Staff',
  '/sustainability': 'Sustainability',
  '/terms': 'Terms',
  '/privacy-policy': 'Privacy Policy',
  '/addon-services': 'Add-on / Special Services',
  '/search': 'Site search',
};

export function resolveHubPageSeoPath(path: string): string {
  return CRAWLER_HUB_PATH_ALIASES[path] ?? path;
}

export function resolveTenantMarketingPageUrl(
  websiteUrl: string | undefined,
  path: string,
  fallbackUrl: string,
): string {
  if (websiteUrl?.trim()) {
    const origin = websiteUrl.replace(/\/$/, '');
    const segment = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${segment}`;
  }
  return fallbackUrl;
}

export function getHubPageDefaultSeo(path: string): { title: string; description: string } {
  const canonical = resolveHubPageSeoPath(path);
  const meta = CRAWLER_HUB_PAGE_META[canonical];
  return meta ? { title: meta.title, description: meta.description } : { title: '', description: '' };
}

export function hasHubPageSeoOverride(
  path: string,
  hubPageSeo?: Partial<Record<string, HubPageSeoEntry>>,
): boolean {
  const canonical = resolveHubPageSeoPath(path);
  const entry = hubPageSeo?.[canonical];
  return Boolean(entry?.title?.trim() || entry?.description?.trim());
}

/**
 * Tenant-safe hub copy.
 *
 * The legacy corpus describes one specific cleaning company in Kochi, trading
 * since 2009, with a 4.8 star rating. Substituting the brand name into it leaves
 * every one of those claims in place on someone else's website, so tenants get
 * copy generated from their own name and the page's purpose instead.
 */
export function buildTenantHubPageSeo(
  path: string,
  companyName: string,
): { title: string; description: string } {
  const canonical = resolveHubPageSeoPath(path);
  const label = HUB_PAGE_SEO_LABELS[canonical];

  const templates: Record<string, { title: string; description: string }> = {
    '/faq': {
      title: `${companyName} FAQ | Pricing, Booking & Service Coverage`,
      description: `Answers to common questions about ${companyName} — pricing, service areas, what each service includes, plans, insurance and how to book.`,
    },
    '/our-story': {
      title: `About ${companyName} | Our Story`,
      description: `How ${companyName} works, what we stand for, and the team behind the service.`,
    },
    '/contact-us': {
      title: `Contact ${companyName} | Enquiries & Bookings`,
      description: `Get in touch with ${companyName} for a quote, a booking, or help with an existing job.`,
    },
    '/testimonials': {
      title: `${companyName} Reviews & Testimonials`,
      description: `What customers say about working with ${companyName}.`,
    },
    '/careers': {
      title: `Careers at ${companyName} | Open Roles`,
      description: `Current openings at ${companyName} and what it is like to work with our team.`,
    },
    '/terms': {
      title: `Terms of Service | ${companyName}`,
      description: `The terms that apply when you book a service with ${companyName}.`,
    },
    '/privacy-policy': {
      title: `Privacy Policy | ${companyName}`,
      description: `How ${companyName} collects, uses and protects your personal information.`,
    },
    '/search': {
      title: `Search ${companyName} | Services & Areas`,
      description: `Search ${companyName} service and location pages, then book online.`,
    },
  };

  const template = templates[canonical];
  if (template) return template;

  if (label) {
    return {
      title: `${label} | ${companyName}`,
      description: `${label} from ${companyName}. Get details, pricing and availability, or book online.`,
    };
  }

  return { title: companyName, description: `Services and booking information from ${companyName}.` };
}

export function resolveHubPageSeoCopy(
  path: string,
  defaults: { title: string; description: string },
  branding: { phone?: string; email?: string; companyName?: string },
  hubPageSeo?: Partial<Record<string, HubPageSeoEntry>>,
): { title: string; description: string } {
  const canonical = resolveHubPageSeoPath(path);
  const override = hubPageSeo?.[canonical];

  // A super-admin or org override always wins; only the fallback needs guarding.
  const tenantDefaults = isTenantMarketingBrand(branding.companyName)
    ? buildTenantHubPageSeo(canonical, resolveMarketingSiteCompanyName(branding.companyName))
    : defaults;

  const titleSource = override?.title?.trim() || tenantDefaults.title;
  const descriptionSource = override?.description?.trim() || tenantDefaults.description;
  return {
    title: substituteMarketingBrandingInText(titleSource, branding),
    description: substituteMarketingBrandingInText(descriptionSource, branding),
  };
}

/** Default og:image for hub pages (configurable per org/platform). */
export function resolveHubPageOgImageUrl(websiteUrl?: string, defaultOgImageUrl?: string): string {
  const imagePath = resolveDefaultMarketingOgImagePath(defaultOgImageUrl);
  return toAbsoluteSiteUrl(imagePath, resolveMarketingSiteOrigin(websiteUrl));
}
